import React, { useEffect, useMemo, useState } from 'react';
import authService from '../../services/authService';

const AdminUsage = () => {
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [since, setSince] = useState('');
  const [until, setUntil] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [breakdown, setBreakdown] = useState({ totals: null, items: [] });

  const loadUsers = async () => {
    try {
      setLoading(true);
      const t = authService.getToken();
      const q = new URLSearchParams({ page: String(page), pageSize: String(pageSize), ...(search ? { search } : {}) });
      const res = await fetch(`/api/admin/users?${q.toString()}`, { headers: { Authorization: `Bearer ${t}` } });
      if (!res.ok) throw new Error('Failed to load users');
      const data = await res.json();
      setUsers(data.users || []);
    } catch (e) { setError(e.message || 'Failed to load users'); }
    finally { setLoading(false); }
  };

  const loadBreakdown = async () => {
    if (!selectedUser?.id) { setBreakdown({ totals: null, items: [] }); return; }
    try {
      setLoading(true);
      const t = authService.getToken();
      const qs = new URLSearchParams({ userId: selectedUser.id });
      if (since) qs.set('since', since);
      if (until) qs.set('until', until);
      const res = await fetch(`/api/admin/usage/model-breakdown?${qs.toString()}`, { headers: { Authorization: `Bearer ${t}` } });
      const raw = await res.text();
      const ct = res.headers.get('content-type') || '';
      if (!res.ok) throw new Error(raw || `HTTP ${res.status}`);
      const json = ct.includes('application/json') && raw ? JSON.parse(raw) : { totals:null, items:[] };
      setBreakdown({ totals: json.totals || null, items: Array.isArray(json.items) ? json.items : [] });
    } catch (e) { setError(e.message || 'Failed to load usage'); }
    finally { setLoading(false); }
  };

  useEffect(() => { loadUsers(); }, [page, pageSize, search]);
  useEffect(() => { loadBreakdown(); }, [selectedUser, since, until]);

  const totalRow = useMemo(() => breakdown.totals ? [
    breakdown.totals.requests,
    breakdown.totals.inputTokens,
    breakdown.totals.outputTokens,
    breakdown.totals.totalTokens,
    (breakdown.totals.costCents/100).toFixed(2)
  ] : null, [breakdown]);

  return (
    <div className="admin-section">
      <h2 className="admin-header">Token Usage by Model</h2>
      {error && <div className="error-message" style={{ marginBottom: 8 }}>{error}</div>}
      <div className="admin-controls" style={{ gap: 8 }}>
        <label style={{ display:'flex', alignItems:'center', gap:8 }}>
          <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>Search:</span>
          <input 
            type="text" 
            value={search} 
            onChange={e=>{ setSearch(e.target.value); setPage(1); }} 
            placeholder="email or username"
            style={{
              padding: '8px 12px',
              border: '1.5px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '14px',
              transition: 'all 0.2s ease',
              background: '#fff',
              width: '200px',
              boxSizing: 'border-box'
            }}
            onFocus={(e)=>{
              e.target.style.borderColor = '#667eea';
              e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
            }}
            onBlur={(e)=>{
              e.target.style.borderColor = '#e5e7eb';
              e.target.style.boxShadow = 'none';
            }}
          />
        </label>
        <label style={{ display:'flex', alignItems:'center', gap:8 }}>
          <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>Rows:</span>
          <select 
            value={pageSize} 
            onChange={e=>setPageSize(Number(e.target.value))}
            style={{
              padding: '8px 12px',
              border: '1.5px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '14px',
              transition: 'all 0.2s ease',
              background: '#fff',
              cursor: 'pointer',
              minWidth: '80px'
            }}
            onFocus={(e)=>{
              e.target.style.borderColor = '#667eea';
              e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
            }}
            onBlur={(e)=>{
              e.target.style.borderColor = '#e5e7eb';
              e.target.style.boxShadow = 'none';
            }}
          >
            <option value={5}>5</option>
            <option value={10}>10</option>
            <option value={25}>25</option>
          </select>
        </label>
        <div className="admin-pager" style={{ display:'flex', gap:8, alignItems:'center' }}>
          <button 
            disabled={page<=1} 
            onClick={()=>setPage(p=>p-1)}
            style={{
              padding: '6px 12px',
              background: page<=1 ? '#f3f4f6' : '#fff',
              color: page<=1 ? '#9ca3af' : '#667eea',
              border: `1.5px solid ${page<=1 ? '#e5e7eb' : '#667eea'}`,
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: 500,
              cursor: page<=1 ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              opacity: page<=1 ? 0.6 : 1
            }}
            onMouseEnter={(e)=>{
              if (page>1) {
                e.target.style.background = '#667eea';
                e.target.style.color = '#fff';
              }
            }}
            onMouseLeave={(e)=>{
              if (page>1) {
                e.target.style.background = '#fff';
                e.target.style.color = '#667eea';
              }
            }}
          >
            Prev
          </button>
          <span style={{ fontSize:14, color:'#374151' }}>Page {page}</span>
          <button 
            disabled={users.length < pageSize} 
            onClick={()=>setPage(p=>p+1)}
            style={{
              padding: '6px 12px',
              background: users.length < pageSize ? '#f3f4f6' : '#fff',
              color: users.length < pageSize ? '#9ca3af' : '#667eea',
              border: `1.5px solid ${users.length < pageSize ? '#e5e7eb' : '#667eea'}`,
              borderRadius: '6px',
              fontSize: '13px',
              fontWeight: 500,
              cursor: users.length < pageSize ? 'not-allowed' : 'pointer',
              transition: 'all 0.2s ease',
              opacity: users.length < pageSize ? 0.6 : 1
            }}
            onMouseEnter={(e)=>{
              if (users.length >= pageSize) {
                e.target.style.background = '#667eea';
                e.target.style.color = '#fff';
              }
            }}
            onMouseLeave={(e)=>{
              if (users.length >= pageSize) {
                e.target.style.background = '#fff';
                e.target.style.color = '#667eea';
              }
            }}
          >
            Next
          </button>
        </div>
      </div>
      <div className="table-scroll">
        <table className="admin-table">
          <thead><tr><th>Select</th><th>Email</th><th>Name</th><th>Roles</th></tr></thead>
          <tbody>
            {users.map(u => (
              <tr key={u.id} className={selectedUser?.id===u.id?'active-row':''}>
                <td><input type="radio" name="seluser" checked={selectedUser?.id===u.id} onChange={()=>setSelectedUser(u)} /></td>
                <td>{u.email}</td>
                <td>{(u.firstName||'') + ' ' + (u.lastName||'')}</td>
                <td>{(u.roles||[]).join(', ')}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="admin-controls" style={{ gap: 8, marginTop: 12, alignItems:'baseline', display:'flex', flexWrap:'wrap' }}>
        <div style={{ fontSize: 14, color:'#555' }}>For: <strong>{selectedUser ? selectedUser.email : '—'}</strong></div>
        <label style={{ display:'flex', alignItems:'center', gap:8 }}>
          <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>Since</span>
          <input 
            type="date" 
            value={since} 
            onChange={e=>setSince(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1.5px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '14px',
              transition: 'all 0.2s ease',
              background: '#fff',
              cursor: 'pointer'
            }}
            onFocus={(e)=>{
              e.target.style.borderColor = '#667eea';
              e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
            }}
            onBlur={(e)=>{
              e.target.style.borderColor = '#e5e7eb';
              e.target.style.boxShadow = 'none';
            }}
          />
        </label>
        <label style={{ display:'flex', alignItems:'center', gap:8 }}>
          <span style={{ fontSize:14, fontWeight:500, color:'#374151' }}>Until</span>
          <input 
            type="date" 
            value={until} 
            onChange={e=>setUntil(e.target.value)}
            style={{
              padding: '8px 12px',
              border: '1.5px solid #e5e7eb',
              borderRadius: '8px',
              fontSize: '14px',
              transition: 'all 0.2s ease',
              background: '#fff',
              cursor: 'pointer'
            }}
            onFocus={(e)=>{
              e.target.style.borderColor = '#667eea';
              e.target.style.boxShadow = '0 0 0 3px rgba(102, 126, 234, 0.1)';
            }}
            onBlur={(e)=>{
              e.target.style.borderColor = '#e5e7eb';
              e.target.style.boxShadow = 'none';
            }}
          />
        </label>
        <span style={{ fontSize:12, color:'#777' }}>{loading ? 'Loading…' : ''}</span>
      </div>

      <div className="table-scroll" style={{ marginTop: 12 }}>
        <table className="admin-table">
          <thead>
            <tr>
              <th>Provider</th>
              <th>Model</th>
              <th>Requests</th>
              <th>In</th>
              <th>Out</th>
              <th>Total</th>
              <th>Cost ($)</th>
            </tr>
          </thead>
          <tbody>
            {(breakdown.items||[]).length === 0 && (
              <tr><td colSpan="7" style={{ textAlign:'center' }}>{selectedUser ? 'No usage for filters' : 'Select a user'}</td></tr>
            )}
            {(breakdown.items||[]).map((r, idx) => (
              <tr key={idx}>
                <td>{r.provider || '-'}</td>
                <td>{r.model || '-'}</td>
                <td>{r.requests}</td>
                <td>{r.inputTokens}</td>
                <td>{r.outputTokens}</td>
                <td>{r.totalTokens}</td>
                <td>{(r.costCents/100).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
          {totalRow && (
            <tfoot>
              <tr>
                <th colSpan="2" style={{ textAlign:'right' }}>Totals</th>
                <th>{totalRow[0]}</th>
                <th>{totalRow[1]}</th>
                <th>{totalRow[2]}</th>
                <th>{totalRow[3]}</th>
                <th>{totalRow[4]}</th>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
};

export default AdminUsage;


